import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ScheduleAuditModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    auditType: '',
    framework: '',
    startDate: '',
    endDate: '',
    priority: 'Medium',
    assignedTeam: [],
    description: '',
    milestones: [],
    dependencies: [],
    preparationDays: '30'
  });

  const [currentTeamMember, setCurrentTeamMember] = useState('');
  const [currentMilestone, setCurrentMilestone] = useState({ name: '', date: '' });
  const [currentDependency, setCurrentDependency] = useState('');

  const departments = [
    'Finance', 'IT', 'Legal', 'Human Resources', 'Operations', 
    'Facilities', 'Procurement', 'Quality Assurance', 'Marketing', 'Sales'
  ];

  const auditTypes = [
    'Financial', 'Security', 'Privacy', 'Operational', 'Compliance', 
    'Environmental', 'Quality', 'Risk Assessment', 'Internal'
  ];

  const frameworks = [
    'SOX', 'ISO 27001', 'GDPR', 'COSO', 'NIST', 'ITIL', 'EPA', 'ISO 9001', 'Employment Law'
  ];

  const priorities = ['Low', 'Medium', 'High', 'Critical'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTeamMember = () => {
    if (currentTeamMember?.trim() && !formData?.assignedTeam?.includes(currentTeamMember?.trim())) {
      setFormData(prev => ({
        ...prev,
        assignedTeam: [...prev?.assignedTeam, currentTeamMember?.trim()]
      }));
      setCurrentTeamMember('');
    }
  };

  const handleRemoveTeamMember = (member) => {
    setFormData(prev => ({
      ...prev,
      assignedTeam: prev?.assignedTeam?.filter(m => m !== member)
    }));
  };

  const handleAddMilestone = () => {
    if (currentMilestone?.name?.trim() && currentMilestone?.date) {
      setFormData(prev => ({
        ...prev,
        milestones: [...prev?.milestones, { ...currentMilestone, status: 'Pending' }]
      }));
      setCurrentMilestone({ name: '', date: '' });
    }
  };

  const handleRemoveMilestone = (index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev?.milestones?.filter((_, i) => i !== index)
    }));
  };

  const handleAddDependency = () => {
    if (currentDependency?.trim() && !formData?.dependencies?.includes(currentDependency?.trim())) {
      setFormData(prev => ({
        ...prev,
        dependencies: [...prev?.dependencies, currentDependency?.trim()]
      }));
      setCurrentDependency('');
    }
  };

  const handleRemoveDependency = (dependency) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev?.dependencies?.filter(d => d !== dependency)
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Validate required fields
    if (!formData?.title || !formData?.department || !formData?.auditType || !formData?.startDate || !formData?.endDate) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate date range
    if (new Date(formData?.startDate) >= new Date(formData?.endDate)) {
      alert('End date must be after start date');
      return;
    }

    // Create new audit object
    const newAudit = {
      ...formData,
      id: `AUD-${Math.random()?.toString(36)?.substr(2, 3)?.toUpperCase()}`,
      status: 'Scheduled',
      preparationStatus: 0,
      milestones: formData?.milestones?.length > 0 ? formData?.milestones : [
        { name: 'Planning Complete', date: '', status: 'Pending' },
        { name: 'Fieldwork Complete', date: '', status: 'Pending' },
        { name: 'Final Report', date: '', status: 'Pending' }
      ]
    };

    onSave(newAudit);
    onClose();
    
    // Reset form
    setFormData({
      title: '',
      department: '',
      auditType: '',
      framework: '',
      startDate: '',
      endDate: '',
      priority: 'Medium',
      assignedTeam: [],
      description: '',
      milestones: [],
      dependencies: [],
      preparationDays: '30'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-dark-elevated rounded-neumorphic shadow-neumorphic w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-surface">
          <h2 className="text-xl font-semibold text-text-dark-primary">Schedule New Audit</h2>
          <button
            onClick={onClose}
            className="neumorphic-button p-2 text-text-dark-secondary hover:text-text-dark-primary"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Audit Title *
              </label>
              <input
                type="text"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                placeholder="Enter audit title"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Department *
              </label>
              <select
                value={formData?.department}
                onChange={(e) => handleInputChange('department', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                required
              >
                <option value="">Select department</option>
                {departments?.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Audit Type *
              </label>
              <select
                value={formData?.auditType}
                onChange={(e) => handleInputChange('auditType', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                required
              >
                <option value="">Select audit type</option>
                {auditTypes?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Framework
              </label>
              <select
                value={formData?.framework}
                onChange={(e) => handleInputChange('framework', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
              >
                <option value="">Select framework</option>
                {frameworks?.map(framework => (
                  <option key={framework} value={framework}>{framework}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Priority
              </label>
              <select
                value={formData?.priority}
                onChange={(e) => handleInputChange('priority', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
              >
                {priorities?.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Start Date *
              </label>
              <input
                type="date"
                value={formData?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                End Date *
              </label>
              <input
                type="date"
                value={formData?.endDate}
                onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Preparation Days
              </label>
              <input
                type="number"
                value={formData?.preparationDays}
                onChange={(e) => handleInputChange('preparationDays', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                min="1"
                max="365"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-dark-primary mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              className="neumorphic-input w-full px-3 py-2 h-32 resize-none"
              placeholder="Enter audit description and objectives"
            />
          </div>

          {/* Assigned Team */}
          <div>
            <label className="block text-sm font-medium text-text-dark-primary mb-2">
              Assigned Team
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={currentTeamMember}
                onChange={(e) => setCurrentTeamMember(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddTeamMember())}
                className="neumorphic-input flex-1 px-3 py-2"
                placeholder="Add team member"
              />
              <button
                type="button"
                onClick={handleAddTeamMember}
                className="btn-secondary-dark px-3 py-2"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData?.assignedTeam?.map(member => (
                <span key={member} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-neon-green bg-opacity-20 text-neon-green">
                  {member}
                  <button
                    type="button"
                    onClick={() => handleRemoveTeamMember(member)}
                    className="ml-1 text-neon-green hover:text-white"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Milestones */}
          <div>
            <label className="block text-sm font-medium text-text-dark-primary mb-2">
              Milestones
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <input
                type="text"
                value={currentMilestone?.name}
                onChange={(e) => setCurrentMilestone(prev => ({ ...prev, name: e?.target?.value }))}
                className="neumorphic-input px-3 py-2"
                placeholder="Milestone name"
              />
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={currentMilestone?.date}
                  onChange={(e) => setCurrentMilestone(prev => ({ ...prev, date: e?.target?.value }))}
                  className="neumorphic-input flex-1 px-3 py-2"
                />
                <button
                  type="button"
                  onClick={handleAddMilestone}
                  className="btn-secondary-dark px-3 py-2"
                >
                  Add
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {formData?.milestones?.map((milestone, index) => (
                <div key={index} className="flex items-center justify-between neumorphic-card p-3">
                  <div>
                    <span className="text-text-dark-primary font-medium">{milestone?.name}</span>
                    <span className="text-text-dark-secondary text-sm ml-2">{milestone?.date}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveMilestone(index)}
                    className="text-error-dark hover:text-error-light"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Dependencies */}
          <div>
            <label className="block text-sm font-medium text-text-dark-primary mb-2">
              Dependencies
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={currentDependency}
                onChange={(e) => setCurrentDependency(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddDependency())}
                className="neumorphic-input flex-1 px-3 py-2"
                placeholder="Add dependency (e.g., AUD-001)"
              />
              <button
                type="button"
                onClick={handleAddDependency}
                className="btn-secondary-dark px-3 py-2"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData?.dependencies?.map(dependency => (
                <span key={dependency} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-neon-purple bg-opacity-20 text-neon-purple">
                  {dependency}
                  <button
                    type="button"
                    onClick={() => handleRemoveDependency(dependency)}
                    className="ml-1 text-neon-purple hover:text-white"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Modal Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-dark-surface">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary-dark px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-dark px-4 py-2 inline-flex items-center space-x-2"
            >
              <Icon name="Calendar" size={16} />
              <span>Schedule Audit</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleAuditModal;